import React, { useState, useEffect, useRef } from "react";
import jsQR from "jsqr";
import api from '../api';
import useAuth from "../../hooks/useAuth";
import StartShiftCompliance from "./StartShiftCompliance";
import CryptoJS from "crypto-js";

/** Reverse geocode */
async function reverseGeo(lat, lng) {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
      { headers: { "User-Agent": "attendance-app" } }
    );
    const data = await res.json();
    return {
      city: data.address?.city || data.address?.town,
      locality: data.address?.suburb || data.address?.neighbourhood,
      state: data.address?.state,
    };
  } catch {
    return null;
  }
}

export default function Attendance({ empId, siteId, shiftId }) {
  const [aiMode, setAiMode] = useState(null);
  const [qrVerified, setQrVerified] = useState(false);
  const [attendanceChecked, setAttendanceChecked] = useState(false);
  const [step, setStep] = useState(0); 
  const [images, setImages] = useState([]); 
  const [results, setResults] = useState({}); 
  const [cameraStarted, setCameraStarted] = useState(false);
  const [showShift, setShowShift] = useState(false);
  const [selfieRequired, setSelfieRequired] = useState(false);
  const [riskScore, setRiskScore] = useState(0);
  const [locationMeta, setLocationMeta] = useState(null);
  
// null | "SCAN" | "SHIFT" | "CONCERN"

  const videoRef = useRef(null);
  const scanningRef = useRef(true);
  const canvasRef = useRef(document.createElement("canvas"));
  const streamRef = useRef(null);

  const { auth } = useAuth();

  const stepInstructions = [
    "सामने का दृश्य: जैकेट + कैप / Front view: Jacket + Cap",
    "साइड व्यू: हेयर नॉट / Side view: Hair knot",
    "नीचे का हिस्सा: बेल्ट, पैंट, जूते / Lower body: Belt, Pants, Shoes",
  ];

  // Detect device and AI mode
  useEffect(() => {
    const ua = navigator.userAgent;
    const isIOS = /iP(hone|ad|od)/.test(ua);
    const iosVersionMatch = ua.match(/OS (\d+)_/);
    const iosVersion = iosVersionMatch ? parseInt(iosVersionMatch[1], 10) : null;

    const supportsWebGL = (() => {
      try {
        const canvas = document.createElement("canvas");
        return !!(window.WebGLRenderingContext &&
          (canvas.getContext("webgl") || canvas.getContext("experimental-webgl")));
      } catch { return false; }
    })();

    if (isIOS && iosVersion <= 12) setAiMode("SERVER_AI");
    else if (supportsWebGL) setAiMode("CLIENT_AI");
    else setAiMode("SERVER_AI");
  }, []);

  // Auto-start camera when selfie required
  useEffect(() => {
    if (selfieRequired && step === 1 && !cameraStarted) {
      setCameraStarted(true);
      scanningRef.current = false; 
    }
  }, [selfieRequired, step]);

useEffect(() => {
  if (!cameraStarted || !selfieRequired) return;
  
  const startSelfieCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1080 } }
      });
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      streamRef.current = stream;
    } catch (err) {
      console.error("Selfie camera failed", err);
      alert("Camera access failed for selfie");
    }
  };
  
  startSelfieCamera();
}, [cameraStarted, selfieRequired]);


  // Check if attendance already marked
  useEffect(() => {
    const checkAttendance = async () => {
      try {
        const { data } = await api.get(
          `${process.env.REACT_APP_URL}/attendance/attendance-status`,
          { params: { empId, siteId, shiftId } }
        );
        if (data.attendanceMarked) setQrVerified(true);
        setAttendanceChecked(true);
      } catch (err) {
        console.error("Attendance check failed", err);
        setAttendanceChecked(true);
      }
    };
    checkAttendance();
  }, [empId, siteId, shiftId]);

  // Start camera (prefer wide-angle / max resolution)
  useEffect(() => {
    if (!attendanceChecked || !cameraStarted) return;

    let rafId;
    const startCamera = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(d => d.kind === "videoinput");
        let preferredDeviceId = videoDevices[0]?.deviceId;

        // Try selecting wide-angle camera if possible
        if (videoDevices.length > 1) {
          preferredDeviceId = videoDevices[videoDevices.length - 1].deviceId; // heuristic
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { exact: preferredDeviceId },
            width: { ideal: 3840 },
            height: { ideal: 2160 },
            facingMode: { ideal: "environment"},
            //advanced: [{ zoom: 1 }] // requests wide angle if device supports
          }
        });

        streamRef.current = stream;
        videoRef.current.srcObject = stream;
        await videoRef.current.play();

        const scanQR = () => {
          if (!scanningRef.current) return;

          const video = videoRef.current;
          if (!video || video.readyState !== video.HAVE_ENOUGH_DATA) {
            rafId = requestAnimationFrame(scanQR);
            return;
          }

          const canvas = canvasRef.current;
          const ctx = canvas.getContext("2d", { willReadFrequently: true });
          const w = video.videoWidth;
          const h = video.videoHeight;
          canvas.width = 800;
          canvas.height = 800;
          const cropSize = Math.min(w, h) * 0.8;

          ctx.drawImage(video, (w - cropSize) / 2, (h - cropSize) / 2, cropSize, cropSize, 0, 0, canvas.width, canvas.height);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(imageData.data, canvas.width, canvas.height, { inversionAttempts: "attemptBoth" });

          if (code?.data) {
            scanningRef.current = false;
            verifyQR(code.data);
            return;
          }
          rafId = requestAnimationFrame(scanQR);
        };
        rafId = requestAnimationFrame(scanQR);
      } catch (err) {
        console.error("Camera error:", err);
        alert("Camera access failed");
      }
    };
    startCamera();

    return () => {
      cancelAnimationFrame(rafId);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    };
  }, [attendanceChecked, qrVerified, cameraStarted]);

  // --- QR Verification logic (same as before) ---
  const MAX_DISTANCE_METERS = 85;
  function getDistanceMeters(lat1, lon1, lat2, lon2) {
    const R = 6371000;
    const toRad = deg => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  const QR_SECRET = "SITE_QR_AES_SECRET_2025";
  const QR_IV = CryptoJS.enc.Utf8.parse("1234567890abcdef");

  const normalizeBase64 = (input) => input.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(input.length / 4) * 4, "=");

  const decryptQRData = (encryptedText) => {
    const key = CryptoJS.SHA256(QR_SECRET);
    const normalized = normalizeBase64(encryptedText);
    const bytes = CryptoJS.AES.decrypt(normalized, key, {
      iv: QR_IV,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    if (!decrypted) throw new Error("Invalid QR / Decryption failed");
    return JSON.parse(decrypted);
  };

  const calculateRisk = ({ distance, isNewDevice, isNightShift, randomCheck }) => {
    let score = 0;
    if (distance > 50) score += 5;
    if (isNewDevice) score += 3;
    if (isNightShift) score += 2;
    if (randomCheck) score += 4;
    return score;
  };

  const verifyQR = async (qrData) => {
    try {
      const decrypted = decryptQRData(qrData);
      const today = new Date().toISOString().slice(0, 10);
      //if (decrypted.date !== today) return alert("QR expired");
      setLocationMeta(await reverseGeo(decrypted.siteLat, decrypted.siteLng));

         const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      });
    });

    const userLat = position.coords.latitude;
    const userLng = position.coords.longitude;

    const distance = getDistanceMeters(
      userLat,
      userLng,
      decrypted.siteLat, 
      decrypted.siteLng
    );

    console.log("📍 Distance from site:", Math.round(distance), "meters");

    if (distance > MAX_DISTANCE_METERS) {
      alert(
        `You are too far from the site.\nDistance: ${Math.round(
          distance
        )}m`
      );
      return;
    }

    // ✅ VERIFIED
    console.log("✅ QR + GEO VERIFIED");

      const risk = calculateRisk({
        distance: 0,
        isNewDevice: true,
        isNightShift: false,
        randomCheck: false,
      });

      setRiskScore(risk);

      if (risk >= 0) {
        setSelfieRequired(true);
        setStep(1);
        setCameraStarted(true); // start camera for selfie
      } else {
        setQrVerified(true);
        setShowShift(true);
      }


    } catch (err) {
      console.error("QR verification failed:", err);
      alert("Invalid QR");
    }
  };

  // --- Capture Step with metadata ---
  const captureStep = async () => {
  if (!videoRef.current) return;

  // Canvas capture
  const canvas = document.createElement("canvas");
  canvas.width = videoRef.current.videoWidth;
  canvas.height = videoRef.current.videoHeight;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

  const imageDataUrl = canvas.toDataURL("image/jpeg", 0.85);

  // Metadata
  const timestamp = new Date().toISOString();
  let gps = null;
  try {
    const pos = await new Promise((res, rej) =>
      navigator.geolocation.getCurrentPosition(res, rej, { enableHighAccuracy: true, timeout: 8000 })
    );
    gps = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
  } catch {}

  const geo = gps ? await reverseGeo(gps.lat, gps.lng) : null;

  // Payload
  const payload = { step, image: imageDataUrl, capturedAt: timestamp, gps, geo };
  setImages(prev => [...prev, payload]);

  // --- SERVER AI: check if wide-angle image is sufficient
  let smartSkip = false;
  if (aiMode === "SERVER_AI") {
    const { data } = await api.post(`${process.env.REACT_APP_URL}/attendance/server-ai`, payload);
    setResults(prev => ({ ...prev, [`step${step}`]: data }));

    // AI returns { capOk, jacketOk, shirtOk, faceOk }
    if (data.capOk && data.jacketOk && data.shirtOk && data.faceOk) {
      smartSkip = true;
    }
  }

  // Decide next step
  if (smartSkip) {
    setStep(3); // skip remaining steps
  } else if (step < 3) {
    setStep(step + 1);
  } else {
    setQrVerified(true);
    setShowShift(true);
  }
};


  const stopCamera = () => {
    const stream = videoRef.current?.srcObject;
    if (stream) stream.getTracks().forEach(track => track.stop());
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const markAttendance = async () => {
    await api.post(`${process.env.REACT_APP_URL}/attendance/mark-attendance`, {
      empId, siteId, shiftId, images, results, aiMode, riskScore, locationMeta,
    });
    alert("Attendance marked!");
    stopCamera();
  };

  if (!attendanceChecked) return <div>Loading...</div>;

  return (
    <div  style={{
            padding: "12px",
            fontFamily: "'Roboto', sans-serif",
            //display: "flex",
            flexDirection: "column",
            alignItems: "center", }}>
       <img
          src="/faces/sentinels.png"   // <-- replace with your logo path
          alt="Brand Logo"
          style={{
            height: "70px",
            marginBottom: "12px",
            objectFit: "contain",
          }}
        />
      <h2 style={{ textAlign: "center", marginBottom: "20px" }}>उपस्थिति / Attendance</h2>

     
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "16px", marginBottom: "20px" }}>
          {/*
          <ActionCard
            title="साइट QR स्कैन करें"
            subtitle="Scan site/location QR code"
            icon="📷"
            onClick={() => {
              setQrVerified(false);
              setCameraStarted(true);
              setShowShift(false);
            }}
          /> */}
          <ActionCard
            title="शिफ्ट शुरू करें"
            subtitle="Start shift"
            icon="🕒"
              onClick={() => {
                setQrVerified(true);
                setCameraStarted(true);
              setShowShift(true);
            }}
          />
       
        </div>  

      {(!qrVerified && cameraStarted) && (
        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <video ref={videoRef}  playsInline muted autoPlay style={{ width: "100%", maxWidth: "600px", borderRadius: "16px", boxShadow: "0 10px 25px rgba(0,0,0,0.2)" }} />
        </div>
      )}

      {(selfieRequired && qrVerified && step > 0 && step <= 3) && (
        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <h3>Step {step}</h3>
          <p>{stepInstructions[step - 1]}</p>
          
          <video ref={videoRef} playsInline muted autoPlay style={{ width: "100%", maxWidth: "400px", borderRadius: "12px", border: "2px solid #f44336", boxShadow: "0 6px 20px rgba(0,0,0,0.15)" }} />
          <button onClick={captureStep} style={{ marginTop: "12px", padding: "12px 24px", fontSize: "16px", borderRadius: "8px", backgroundColor: "#f44336", color: "#fff", border: "none", cursor: "pointer" }}>Capture Step</button>
        </div>
      )}

      {qrVerified && step > 3 && (
        <div style={{ textAlign: "center" }}>
          <h3>Attendance Complete ✅</h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", justifyContent: "center", marginTop: "16px" }}>
            {images.map((img, idx) => <img key={idx} src={img.image} style={{ width: "100px", borderRadius: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.2)" }} />)}
          </div>
          <button onClick={markAttendance} style={{ marginTop: "20px", padding: "12px 32px", fontSize: "16px", borderRadius: "8px", backgroundColor: "#f44336", color: "#fff", border: "none", cursor: "pointer" }}>Mark Attendance</button>
        </div>
      )}

      {showShift && <StartShiftCompliance empId={empId} siteId={siteId} onClose={() => setShowShift(false)} />}
    </div>
  );
}

const ActionCard = ({ title, subtitle, icon, onClick }) => (
  <div onClick={onClick} style={{
    padding: "18px", borderRadius: "16px",
    background: "linear-gradient(135deg, #ffe5e5, #f44336)",
    cursor: "pointer", transition: "all 0.3s ease",
    color: "#fff", display: "flex", flexDirection: "column",
    justifyContent: "center", alignItems: "center", minHeight: "120px",
    boxShadow: "0 8px 20px rgba(0,0,0,0.15)", textAlign: "center", fontWeight: "600"
  }}>
    <div style={{ fontSize: "32px", marginBottom: "8px" }}>{icon}</div>
    <div style={{ fontSize: "18px", marginBottom: "4px" }}>{title}</div>
    <div style={{ fontSize: "14px", opacity: 0.9 }}>{subtitle}</div>
  </div>
);
